import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import { User } from '../types/User';
import { Search, Filter, SortAsc, MessageCircle, UserPlus, Calendar, X, ChevronDown } from 'lucide-react';
import MeetingScheduleModal from './MeetingScheduleModal';
import { NetworkingService } from '../services/networkingService';
import { MeetingService } from '../services/meetingService';
import FavoritesModal from './FavoritesModal';
import CustomNotification from './CustomNotification';
import { MessageService } from '../services/messageService';
import Pagination from './Pagination';

interface ParticipantsListProps {
  currentUser: User;
  onSelectUser: (user: User) => void;
  participantsData?: User[];
  participantsLoaded?: boolean;
  meetingsData?: any[];
  meetingsLoaded?: boolean;
  onParticipantsUpdate?: (participants: User[]) => void;
  onMeetingsUpdate?: (meetings: any[]) => void;
  hasMoreParticipants?: boolean;
  loadingMoreParticipants?: boolean;
  loadMoreParticipants?: () => void;
}

const ParticipantsList: React.FC<ParticipantsListProps> = ({ 
  currentUser, 
  onSelectUser, 
  participantsData, 
  participantsLoaded, 
  meetingsData,
  meetingsLoaded,
  onParticipantsUpdate,
  onMeetingsUpdate,
  hasMoreParticipants = false,
  loadingMoreParticipants = false,
  loadMoreParticipants
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [participants, setParticipants] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isFavoritesModalOpen, setIsFavoritesModalOpen] = useState(false);
  const [selectedFavoriteUser, setSelectedFavoriteUser] = useState<User | null>(null);
  const [showSortModal, setShowSortModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedUserForModal, setSelectedUserForModal] = useState<User | null>(null);
  const [sortBy, setSortBy] = useState<'name' | 'company' | 'position' | 'country' | 'recently-joined'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [companyFilter, setCompanyFilter] = useState('');
  const [conversations, setConversations] = useState<any[]>([]);
  const [userMeetings, setUserMeetings] = useState<any[]>([]);
  const [userConnections, setUserConnections] = useState<any[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [animationPlayed, setAnimationPlayed] = useState(false);
  const [visibleParticipants, setVisibleParticipants] = useState<number>(0);
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({ show: false, message: '', type: 'info' });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);
  const hasLoadedRef = useRef(false);

  // Load participants for current page
  const loadParticipantsPage = useCallback(async (page: number) => {
    if (pageLoading) {
      console.log('Already loading page, skipping...');
      return;
    }
    
    setPageLoading(true);
    try {
      console.log(`Loading participants page ${page}...`);
      const result = await NetworkingService.getParticipants(currentUser.id, page - 1, 30);
      console.log(`Got ${result.users.length} users from API`);
      setParticipants(result.users);
      setTotalParticipants(result.totalCount);
      setTotalPages(Math.ceil(result.totalCount / 30));
      setCurrentPage(page);
      setLoading(false); // Ensure loading is set to false
      
      // Show all participants immediately
      setVisibleParticipants(result.users.length);
    } catch (error) {
      console.error('Error loading participants page:', error);
    }
    setPageLoading(false);
  }, [currentUser.id, pageLoading]);

  useEffect(() => {
    // Load first page on component mount - only when currentUser is available
    if (!hasLoadedRef.current && currentUser?.id && !pageLoading && participants.length === 0) {
      console.log('Loading first page of participants...');
      hasLoadedRef.current = true;
      loadParticipantsPage(1);
      setDataLoaded(true);
    }
  }, []); // Empty dependency array - only run once on mount

  useEffect(() => {
    // Load meetings data if available
    if (meetingsData && meetingsLoaded) {
      setUserMeetings(meetingsData);
    }
  }, [meetingsData, meetingsLoaded]);

  // Scroll to top when page changes
  useEffect(() => {
    if (currentPage > 1) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }, [currentPage]);

  // Handle page change
  const handlePageChange = (page: number) => {
    loadParticipantsPage(page);
  };

  // REMOVED: loadAllData - now relies only on Dashboard cached data

  const loadMeetingsAndConnections = async () => {
    try {
      const [meetingsData, connectionsData] = await Promise.all([
        MeetingService.getUserMeetings(currentUser.id),
        NetworkingService.getUserConnections(currentUser.id)
      ]);
      setUserMeetings(meetingsData);
      setUserConnections(connectionsData);
    } catch (error) {
      console.error('Error loading meetings and connections:', error);
    }
  };

  const loadConversations = async () => {
    const convs = await MessageService.getUserConversations(currentUser.id);
    setConversations(convs);
  };

  const loadConversationsLazy = async () => {
    if (!conversationsLoaded && !conversationsLoading) {
      setConversationsLoading(true);
      try {
        const convs = await MessageService.getUserConversations(currentUser.id);
        setConversations(convs);
        setConversationsLoaded(true);
      } catch (error) {
        console.error('Error loading conversations:', error);
      }
      setConversationsLoading(false);
    }
  };

  const getUnreadCount = (userId: string): number => {
    // OPTIMIZATION: If conversations not loaded yet, return 0 (no badge shown)
    if (!conversationsLoaded) return 0;
    
    const conversation = conversations.find(conv => conv.partner?.id === userId);
    return conversation?.unreadCount || 0;
  };

  const [meetingsAndConnectionsLoaded, setMeetingsAndConnectionsLoaded] = useState(false);
  const [participantMeetingStatus, setParticipantMeetingStatus] = useState<Record<string, 'none' | 'pending' | 'confirmed'>>({});
  const [participantFavorites, setParticipantFavorites] = useState<Record<string, boolean>>({});

  const ensureMeetingsAndConnectionsLoaded = async () => {
    if (!meetingsAndConnectionsLoaded) {
      await loadMeetingsAndConnections();
      setMeetingsAndConnectionsLoaded(true);
      
      // Pre-calculate meeting status and favorites for all participants
      const statusMap: Record<string, 'none' | 'pending' | 'confirmed'> = {};
      const favoritesMap: Record<string, boolean> = {};
      
      participants.forEach(participant => {
        const meeting = userMeetings.find(meeting => 
          (meeting.requester_id === participant.id || meeting.recipient_id === participant.id) &&
          (meeting.status === 'pending' || meeting.status === 'confirmed')
        );
        statusMap[participant.id] = meeting ? meeting.status as 'pending' | 'confirmed' : 'none';
        
        const isFavorite = userConnections.some(connection => 
          (connection.requester_id === participant.id || connection.recipient_id === participant.id) &&
          connection.status === 'accepted'
        );
        favoritesMap[participant.id] = isFavorite;
      });
      
      setParticipantMeetingStatus(statusMap);
      setParticipantFavorites(favoritesMap);
    }
  };

  const hasBookedMeeting = (userId: string): boolean => {
    return participantMeetingStatus[userId] !== 'none';
  };

  const getMeetingStatus = (userId: string): 'none' | 'pending' | 'confirmed' => {
    return participantMeetingStatus[userId] || 'none';
  };

  const isInFavorites = (userId: string): boolean => {
    return participantFavorites[userId] || false;
  };

  const filteredAndSortedParticipants = React.useMemo(() => {
    let filtered = participants.filter(participant => {
      const matchesSearch = participant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        participant.company.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCompanyFilter = !companyFilter || 
        participant.company.toLowerCase().includes(companyFilter.toLowerCase());
      
      return matchesSearch && matchesCompanyFilter;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aValue: string;
      let bValue: string;

      switch (sortBy) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'company':
          aValue = a.company;
          bValue = b.company;
          break;
        case 'position':
          aValue = a.position;
          bValue = b.position;
          break;
        case 'country':
          aValue = a.country;
          bValue = b.country;
          break;
        case 'recently-joined':
          // For demo purposes, sort by name as we don't have join dates
          aValue = a.name;
          bValue = b.name;
          break;
        default:
          aValue = a.name;
          bValue = b.name;
      }

      const comparison = aValue.localeCompare(bValue);
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [participants, searchTerm, companyFilter, sortBy, sortDirection]);

  const handleScheduleMeeting = async (user: User) => {
    await ensureMeetingsAndConnectionsLoaded();
    const meetingStatus = getMeetingStatus(user.id);
    if (meetingStatus === 'pending') {
      setNotification({
        show: true,
        message: `Meeting confirmation pending with ${user.name}`,
        type: 'info'
      });
      return;
    } else if (meetingStatus === 'confirmed') {
      setNotification({
        show: true,
        message: `You have already booked a meeting with ${user.name}`,
        type: 'info'
      });
      return;
    }
    setSelectedUserForModal(user);
    setIsScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setIsScheduleModalOpen(false);
    setSelectedUserForModal(null);
  };

  const handleSendConnectionRequest = async (user: User) => {
    await ensureMeetingsAndConnectionsLoaded();
    if (isInFavorites(user.id)) {
      setNotification({
        show: true,
        message: `${user.name} is already in your connections`,
        type: 'info'
      });
      return;
    }
    const result = await NetworkingService.sendConnectionRequest(currentUser.id, user.id);
    if (result.success) {
      // Refresh connections after successful add
      const connectionsData = await NetworkingService.getUserConnections(currentUser.id);
      setUserConnections(connectionsData);
      setParticipantFavorites(prev => ({ ...prev, [user.id]: true }));
      setMeetingsAndConnectionsLoaded(true);
      setNotification({
        show: true,
        message: result.alreadyExists
          ? `${user.name} is already in your connections`
          : 'Successfully connected!',
        type: result.alreadyExists ? 'info' : 'success'
      });
    } else {
      setNotification({
        show: true,
        message: 'Failed to send connection request: ' + result.error,
        type: 'error'
      });
    }
  };

  const handleAddToFavorites = (user: User) => {
    setSelectedFavoriteUser(user);
    setIsFavoritesModalOpen(true);
  };

  const handleCloseFavoritesModal = () => {
    setIsFavoritesModalOpen(false);
    setSelectedFavoriteUser(null);
  };

  const handleFavoriteAdded = async (userId: string) => {
    const connectionsData = await NetworkingService.getUserConnections(currentUser.id);
    setUserConnections(connectionsData);
    setParticipantFavorites(prev => ({ ...prev, [userId]: true }));
    setMeetingsAndConnectionsLoaded(true);
  };

  const handleClearAllFilters = () => {
    setCompanyFilter('');
    setSortBy('name');
    setSortDirection('asc');
  };

  const getSortLabel = () => {
    const labels = {
      'name': 'Name',
      'company': 'Company',
      'position': 'Position',
      'country': 'Country',
      'recently-joined': 'Recently joined'
    };
    return labels[sortBy];
  };

  // Show loading state immediately if data is not loaded yet
  if (!dataLoaded || loading) {
    return (
      <div className="p-4 lg:p-6 bg-gradient-to-br from-gray-50/50 to-white">
        <div className="flex items-center justify-center h-full">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-600"></div>
            <div className="text-gray-500">Loading participants...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 bg-gradient-to-br from-gray-50/50 to-white">
      {/* Search and Filters */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex-1 max-w-md relative mr-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search participants..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl bg-white shadow-md focus:outline-none focus:ring-1 focus:ring-yellow-600 transition-all duration-300 text-gray-600 placeholder-gray-400 text-sm lg:text-base placeholder:text-sm lg:placeholder:text-base"
          />
        </div>
        
        <div className="flex space-x-2 lg:space-x-3">
          <button 
            onClick={() => setShowFilterModal(true)}
            className="flex items-center space-x-1 lg:space-x-2 px-2 lg:px-4 py-3 border border-gray-200 rounded-xl bg-white/80 backdrop-blur-sm shadow-sm hover:bg-gray-50 hover:shadow-md transition-all duration-300 group"
          >
            <Filter className="w-4 h-4 group-hover:scale-110 transition-transform duration-300" style={{ color: '#C89116' }} />
            <span className="font-medium hidden sm:inline">Filter</span>
          </button>
          <button 
            onClick={() => setShowSortModal(true)}
            className="flex items-center space-x-1 lg:space-x-2 px-2 lg:px-4 py-3 border border-gray-200 rounded-xl bg-white/80 backdrop-blur-sm shadow-sm hover:bg-gray-50 hover:shadow-md transition-all duration-300 group"
          >
            <SortAsc className="w-4 h-4 group-hover:scale-110 transition-transform duration-300" style={{ color: '#C89116' }} />
            <span className="font-medium hidden sm:inline">Sort</span>
          </button>
        </div>
      </div>

      {/* Participants List */}
      <div className="flex-1 overflow-y-auto">
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {filteredAndSortedParticipants.slice(0, visibleParticipants).map((participant, index) => (
          <div 
            key={participant.id} 
            className={`border-b border-gray-200 last:border-b-0 p-4 lg:p-6 cursor-pointer transition-all duration-200 hover:bg-yellow-50 ${
              selectedUserId === participant.id ? 'bg-yellow-50 shadow-md' : ''
            }`}
            onClick={() => {
              setSelectedUserId(participant.id);
              onSelectUser(participant);
            }}
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center space-x-4 min-w-0">
                <div className="w-12 lg:w-16 h-12 lg:h-16 rounded-full flex items-center justify-center shadow-sm transition-all duration-300 text-gray-600 font-semibold text-sm lg:text-lg bg-gray-200 flex-shrink-0">
                  {participant.avatar ? (
                    <img 
                      src={participant.avatar} 
                      alt={participant.name}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <span>
                      {participant.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-lg lg:text-xl transition-colors duration-300 text-gray-800 break-words">
                    {participant.name}
                  </h3>
                  <p className="text-gray-600 font-medium text-sm truncate">{participant.position}</p>
                  <p className="text-gray-500 text-sm font-medium italic">
                    {participant.company}
                  </p>
                  <p className="text-gray-400 text-sm flex items-center truncate">
                    {participant.country}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center justify-center space-x-4 mt-3 lg:mt-0 lg:space-x-2">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectUser(participant);
                    // OPTIMIZATION: Load conversations on demand when clicking message button
                    loadConversationsLazy().then(() => {
                    MessageService.markMessagesAsRead(currentUser.id, participant.id).then(() => {
                        loadConversations(); // Refresh after marking as read
                      });
                    });
                  }}
                  className="p-2 rounded-full transition-all duration-300 hover:shadow-lg bg-white hover:bg-white relative"
                  style={{ 
                    color: 'rgb(71 70 70)',
                    border: '1px solid rgb(255 255 255 / var(--tw-bg-opacity, 1))'
                  }}
                  title="Send Message"
                >
                  <MessageCircle style={{ width: '1.5rem', height: '1.5rem' }} />
                  {getUnreadCount(participant.id) > 0 && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
                      <span className="text-xs font-bold text-white">{getUnreadCount(participant.id)}</span>
                    </div>
                  )}
                </button>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToFavorites(participant);
                  }}
                  className={`p-2 rounded-full transition-all duration-300 hover:shadow-lg bg-white hover:bg-white ${
                    isInFavorites(participant.id) ? 'text-green-600' : ''
                  }`}
                  style={{ 
                    color: isInFavorites(participant.id) ? '#16a34a' : 'rgb(71 70 70)',
                    border: '1px solid rgb(255 255 255 / var(--tw-bg-opacity, 1))'
                  }}
                  title={isInFavorites(participant.id) ? "Already in Connections" : "Add to Connections"}
                >
                  <UserPlus style={{ width: '1.5rem', height: '1.5rem' }} />
                </button>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleScheduleMeeting(participant);
                  }}
                  className="p-2 rounded-full transition-all duration-300 hover:shadow-lg bg-white hover:bg-white"
                  style={{ 
                    color: getMeetingStatus(participant.id) === 'confirmed' 
                      ? '#16a34a' 
                      : getMeetingStatus(participant.id) === 'pending'
                      ? '#eab308'
                      : 'rgb(71 70 70)',
                    border: '1px solid rgb(255 255 255 / var(--tw-bg-opacity, 1))'
                  }}
                  title={
                    getMeetingStatus(participant.id) === 'confirmed' 
                      ? "Meeting Booked" 
                      : getMeetingStatus(participant.id) === 'pending'
                      ? "Meeting Pending Confirmation"
                      : "Schedule Meeting"
                  }
                >
                  <Calendar style={{ width: '1.5rem', height: '1.5rem' }} />
                </button>
              </div>
            </div>
          </div>
        ))}
        
        {/* Pagination */}
        {!pageLoading && participants.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={totalParticipants}
            itemsPerPage={30}
          />
        )}
        
        {/* Page Loading Indicator */}
        {pageLoading && (
          <div className="flex justify-center items-center p-4 bg-white border-t border-gray-200">
            <div className="flex items-center space-x-2 text-gray-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-500"></div>
              <span className="text-sm">Loading page...</span>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Meeting Schedule Modal */}
      {isScheduleModalOpen && selectedUserForModal && (
        <MeetingScheduleModal
          participant={selectedUserForModal}
          currentUser={currentUser}
          onClose={handleCloseScheduleModal}
        />
      )}

      {/* Favorites Modal */}
      {isFavoritesModalOpen && selectedFavoriteUser && (
        <FavoritesModal
          user={selectedFavoriteUser}
          currentUser={currentUser}
          onClose={handleCloseFavoritesModal}
          onAdded={() => handleFavoriteAdded(selectedFavoriteUser.id)}
        />
      )}

      {/* Sort Modal */}
      {showSortModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-800">SORT</h2>
                <button
                  onClick={handleClearAllFilters}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear All
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Sort contacts by</h3>
                  <div className="relative">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as any)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-600 appearance-none bg-white"
                    >
                      <option value="name">Name</option>
                      <option value="company">Company</option>
                      <option value="position">Position</option>
                      <option value="country">Country</option>
                      <option value="recently-joined">Recently joined</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Sort direction</h3>
                  <div className="relative">
                    <select
                      value={sortDirection}
                      onChange={(e) => setSortDirection(e.target.value as 'asc' | 'desc')}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-600 appearance-none bg-white"
                    >
                      <option value="asc">Ascending</option>
                      <option value="desc">Descending</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowSortModal(false)}
                  className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
                  style={{ backgroundColor: '#C89116' }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-800">FILTERS</h2>
                <button
                  onClick={handleClearAllFilters}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear All
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Company Name</h3>
                  <div className="relative">
                    <input
                      type="text"
                      value={companyFilter}
                      onChange={(e) => setCompanyFilter(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-600"
                      placeholder="Filter by company name..."
                    />
                    {companyFilter && (
                      <button
                        onClick={() => setCompanyFilter('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowFilterModal(false)}
                  className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
                  style={{ backgroundColor: '#C89116' }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Notification */}
      {notification.show && (
        <CustomNotification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification({ show: false, message: '', type: 'info' })}
        />
      )}
    </div>
  );
};

export default memo(ParticipantsList);